/**
 * Shared benefit-redeem card (Fenton 2026-09-09): full state machine in
 * one component, used both on the venue home (inline redeem, no
 * click-through) and on the /benefits page (per-benefit row).
 *
 * States:
 *   idle    - shows a Redeem button
 *   posting - button disabled while the mutation flies
 *   redeemed - "Show this at the bar" proof panel + live elapsed ticker
 *              + countdown until the next anniversary rollover
 *   locked   - already-redeemed-this-period, learnt from a 400 response
 *              carrying nextAvailableAt; shows the same countdown
 *
 * Two visual variants: `variant="row"` (compact, for the /benefits list)
 * and `variant="hero"` (larger, accent-tinted, for the home surface).
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { redeemMyBenefit, RedeemBenefitError, type RedeemMyBenefitOk } from "../lib/api";
import { formatCountdown } from "../lib/redemption-period";
import { shouldPinkLockedIcon } from "../lib/benefit-redeem-decisions";
import { useScreenshotGuard } from "../lib/screenshot-guard";

export type RedeemableBenefit = {
  id: string;
  description: string | null;
  type: string;
  isWelcomeReward: boolean;
  redemptionLimit: number | null;
  iconEmoji?: string | null;
  periodLength?: string;
  tier?: { name: string };
  // Server-populated for signed-in members who've already redeemed this
  // period. When set, the card starts in the locked state directly so the
  // "Are you sure?" modal never fires against an already-used benefit
  // (Fenton 2026-09-17).
  nextAvailableAt?: string | null;
};

type LocalState =
  | { kind: "idle" }
  | { kind: "redeemed"; at: number; nextAvailableAt: string | null }
  | { kind: "locked"; nextAvailableAt: string | null; reason: string };

export function BenefitRedeemCard({
  venueId,
  venueSlug,
  benefit,
  label,
  icon,
  variant = "row",
}: {
  venueId: string;
  /** Used only to scope the Love Inn "Free Local Pint" pink-icon treatment, see shouldPinkLockedIcon. */
  venueSlug?: string;
  benefit: RedeemableBenefit;
  /** Fallback label (type-derived) shown under the description. */
  label: string;
  /** Small icon; passed by the caller so /benefits can supply per-type SVGs. */
  icon?: React.ReactNode;
  variant?: "row" | "hero";
}) {
  const qc = useQueryClient();
  // Seed from the server-provided per-user redemption state. When
  // benefit.nextAvailableAt is set, this member has already redeemed in
  // the current period; the card starts locked (countdown to next period)
  // so the Redeem button + "Are you sure?" modal never render for a
  // benefit that would just 400 anyway (Fenton 2026-09-17).
  const [state, setState] = useState<LocalState>(() =>
    benefit.nextAvailableAt
      ? { kind: "locked", nextAvailableAt: benefit.nextAvailableAt, reason: "Redemption limit reached for this period" }
      : { kind: "idle" },
  );
  // Confirm gate before firing the mutation. Once a redemption is claimed
  // the member burns their period's allocation, no undo, only 5 min to use
  // the proof at the bar. A tap-through by mistake used to cost them the
  // whole month's benefit; the modal makes intent explicit + shows the
  // instructions so there's zero ambiguity when they say Yes.
  const [confirming, setConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: () => redeemMyBenefit(venueId, benefit.id),
    onSuccess: (data: RedeemMyBenefitOk) => {
      setState({ kind: "redeemed", at: Date.now(), nextAvailableAt: data.nextAvailableAt });
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ["benefits", venueId] });
    },
    onError: (e: Error) => {
      setConfirming(false);
      if (e instanceof RedeemBenefitError) {
        setState({ kind: "locked", nextAvailableAt: e.nextAvailableAt, reason: e.message });
      } else {
        setState({ kind: "locked", nextAvailableAt: null, reason: e.message });
      }
    },
  });

  const displayName = benefit.description ?? label;
  const pinkWhenLocked = shouldPinkLockedIcon(benefit, venueSlug);

  if (state.kind === "redeemed") {
    return (
      <RedeemedProof
        name={displayName}
        redeemedAt={state.at}
        variant={variant}
        onExpire={() =>
          setState({
            kind: "locked",
            nextAvailableAt: state.nextAvailableAt,
            reason: "Redeemed this period",
          })
        }
      />
    );
  }

  if (state.kind === "locked" && state.nextAvailableAt) {
    return (
      <LockedCountdown
        name={displayName}
        reason={state.reason}
        nextAvailableAt={state.nextAvailableAt}
        variant={variant}
        pinkIcon={pinkWhenLocked}
      />
    );
  }

  const confirmModal = confirming ? (
    <RedeemConfirmModal
      displayName={displayName}
      redemptionLimit={benefit.redemptionLimit}
      pending={mutation.isPending}
      onCancel={() => setConfirming(false)}
      onConfirm={() => mutation.mutate()}
    />
  ) : null;

  if (variant === "hero") {
    return (
      <>
        {confirmModal}
        <div className="flex items-center gap-3 rounded-[var(--radius)] border border-[var(--accent)]/40 bg-[var(--accent)]/10 p-4">
          <span
            aria-hidden
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-white"
          >
            {benefit.iconEmoji ? (
              <span className="text-lg">{benefit.iconEmoji}</span>
            ) : (
              <GiftIcon className="h-5 w-5" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[var(--foreground)]">
              {benefit.isWelcomeReward ? "Your welcome perk" : "Ready to claim"}
            </p>
            <p className="text-sm font-normal text-white/80">{displayName}</p>
            {state.kind === "locked" && !state.nextAvailableAt && (
              <p className="mt-1 text-xs text-amber-500">{state.reason}</p>
            )}
          </div>
          <button
            onClick={() => setConfirming(true)}
            disabled={mutation.isPending}
            className="shrink-0 rounded-full bg-[var(--accent)] px-4 py-2 text-xs font-bold transition hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--accent-fg)" }}
          >
            {mutation.isPending ? "…" : "Redeem"}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {confirmModal}
      <div className="flex items-start gap-3">
        {benefit.iconEmoji ? (
          <span aria-hidden className="mt-0.5 shrink-0 text-xl">
            {benefit.iconEmoji}
          </span>
        ) : (
          icon && <span className="mt-0.5 shrink-0 text-[var(--venue-accent)]">{icon}</span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--subtle)]">{displayName}</p>
          <p className="text-xs text-[var(--muted)]">
            {label}
            {benefit.redemptionLimit ? ` · redeemable (${benefit.redemptionLimit}/period)` : " · redeemable"}
          </p>
          {state.kind === "locked" && !state.nextAvailableAt && (
            <p className="mt-1 text-xs text-amber-500">{state.reason}</p>
          )}
        </div>
        <button
          onClick={() => setConfirming(true)}
          disabled={mutation.isPending}
          className="shrink-0 rounded-[var(--radius)] bg-[var(--venue-accent)] px-3 py-1.5 text-xs font-semibold transition hover:opacity-90 disabled:opacity-50"
          style={{ color: "var(--venue-text)" }}
        >
          {mutation.isPending ? "…" : "Redeem"}
        </button>
      </div>
    </>
  );
}

// Confirm-before-redeem modal. Redemption is a one-shot per period: mis-tap
// = burn the month's benefit AND only 5 min to actually use the proof at
// the bar. Modal makes intent explicit + spells out the instructions so
// the member knows exactly what happens when they tap Yes.
function RedeemConfirmModal({
  displayName,
  redemptionLimit,
  pending,
  onCancel,
  onConfirm,
}: {
  displayName: string;
  redemptionLimit: number | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="redeem-confirm-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-[var(--border-default)] bg-[var(--surface)] p-5 text-[var(--foreground)] shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--venue-accent)] text-white"
          >
            <GiftIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 id="redeem-confirm-title" className="text-lg font-bold leading-tight">
              Redeem {displayName}?
            </h2>
            <p className="mt-1 text-sm text-white/80">
              Only tap Yes when you&apos;re at the bar and ready to order.
            </p>
          </div>
        </div>

        <ul className="mt-4 space-y-2 rounded-xl border border-[var(--border-default)] bg-[color-mix(in_srgb,var(--foreground)_4%,transparent)] p-4 text-sm">
          <li className="flex items-start gap-2">
            <BulletDot />
            <span>
              You&apos;ll get a <strong>5-minute window</strong> to show the confirmation to bar staff.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <BulletDot />
            <span>
              A live countdown + wall-clock on the screen proves it&apos;s real (screenshots freeze).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <BulletDot />
            <span>
              {redemptionLimit && redemptionLimit > 0
                ? `You can redeem this ${redemptionLimit}× per period. This tap counts.`
                : "You can only redeem this once per period. This tap counts."}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <BulletDot />
            <span>
              Once you tap Yes, the window <strong>starts immediately</strong>. Not yet at the bar? Come back later.
            </span>
          </li>
        </ul>

        {/* Issy 2026-09-17: buttons split 50/50 full-width across the bottom
            instead of natural-width + right-aligned. Grid gives an exact
            split; w-full inside forces each button to fill its cell. */}
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="w-full rounded-full border border-[var(--border-default)] bg-transparent px-4 py-2.5 text-sm font-medium text-[var(--foreground)] transition hover:bg-[var(--surface-2)] disabled:opacity-50"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="w-full rounded-full bg-[var(--venue-accent)] px-5 py-2.5 text-sm font-bold transition hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--venue-text)" }}
          >
            {pending ? "Redeeming…" : "Yes, redeem now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulletDot() {
  return (
    <span
      aria-hidden
      className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--venue-accent)]"
    />
  );
}

// Post-redeem proof screen. Anti-screenshot-fraud design: three live
// elements (elapsed ticker + wall-clock + proof-expires countdown) all
// tick every second, so a screenshot's frozen values give staff three
// independent tells. Proof auto-expires after PROOF_WINDOW_MS so a
// screenshot reused hours later shows the locked/next state instead
// of the "Show at bar" screen. Countdown-to-next moved off this
// screen (belongs on the locked state, this moment is about THIS
// drink). Fenton 2026-09-09 screenshot-abuse concern.
const PROOF_WINDOW_MS = 5 * 60_000;

function RedeemedProof({
  name,
  redeemedAt,
  variant,
  onExpire,
}: {
  name: string;
  redeemedAt: number;
  variant: "row" | "hero";
  onExpire: () => void;
}) {
  // Native anti-screenshot guard: iOS blanks the app window via the
  // UITextField(isSecureTextEntry) trick, Android via FLAG_SECURE. Only
  // active while THIS card is on screen, released the moment the state
  // flips (Done tapped or 5-min expiry).
  useScreenshotGuard();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.max(0, Math.floor((now - redeemedAt) / 1000));
  const elapsedText =
    elapsed < 60 ? `${elapsed}s ago` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s ago`;
  const wallClock = new Date(now).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const expiresInMs = Math.max(0, PROOF_WINDOW_MS - (now - redeemedAt));
  const expiresMin = Math.floor(expiresInMs / 60_000);
  const expiresSec = Math.floor((expiresInMs % 60_000) / 1000);
  const expiresText = `${expiresMin}:${expiresSec.toString().padStart(2, "0")}`;

  // Auto-expire the proof state so a member who redeemed hours ago
  // can't just re-open the app to the "Show at bar" screen. Fires
  // once when the window closes; the parent flips to the locked
  // state (which reads the nextAvailableAt we're passing forward).
  useEffect(() => {
    if (expiresInMs <= 0) onExpire();
  }, [expiresInMs, onExpire]);

  const containerCls =
    variant === "hero"
      ? "flex flex-col items-center gap-3 rounded-[var(--radius)] border border-[var(--accent)]/50 bg-[var(--accent)]/15 p-6 text-center"
      : "flex flex-col items-center gap-3 rounded-[var(--radius)] border border-[var(--venue-accent)]/40 bg-[var(--venue-accent)]/10 p-5 text-center";
  const badgeBg = variant === "hero" ? "bg-[var(--accent)] text-[var(--accent-fg)]" : "bg-[var(--venue-accent)]";
  const expiresColor = expiresInMs < 60_000 ? "text-amber-500" : "text-[var(--foreground)]";

  return (
    <div className={containerCls}>
      <span className={`flex h-12 w-12 items-center justify-center rounded-full text-xl ${badgeBg}`}>
        ✓
      </span>
      <p className="text-base font-bold text-[var(--foreground)]">Redeemed: {name}</p>
      <p className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs text-[var(--subtle)]">
        Show this screen at the bar
      </p>

      {/* Hero countdown, from 5:00 to 0:00, dominates the card so bar
          staff can't miss it. Turns amber under 1 min for urgency. When
          it hits 0:00 the useEffect above fires onExpire(), the card
          transitions to the locked/next-available state. */}
      <div className="mt-1 flex flex-col items-center gap-1">
        <p className="text-[10px] uppercase tracking-wide text-[var(--muted)]">Valid for</p>
        <p className={`text-5xl font-bold tabular-nums leading-none ${expiresColor}`}>
          {expiresText}
        </p>
      </div>

      {/* Anti-screenshot signals: two small live tickers a bartender can
          eyeball to spot a frozen screenshot. A screenshot freezes all
          three (hero countdown + elapsed + wall clock) at the moment it
          was taken; the real screen keeps moving. */}
      <div className="flex items-center gap-4 pt-1 text-[11px] text-[var(--muted)]">
        <span className="tabular-nums">Redeemed <span className="font-semibold text-[var(--foreground)]">{elapsedText}</span></span>
        <span aria-hidden>·</span>
        <span className="tabular-nums">Now <span className="font-semibold text-[var(--foreground)]">{wallClock}</span></span>
      </div>

      {/* Fenton 2026-09-17: text-white per explicit request. Peer-review flag:
          on a hypothetical light-accent venue, white would fail contrast
          against a light pink bg. All live venues today (Love Inn, Prospect,
          etc.) are dark-accent so it works. If a light-accent venue joins,
          swap to text-[var(--accent-fg)] which auto-adapts to accent luminance. */}
      <button
        onClick={onExpire}
        className="mt-2 rounded-full border border-[var(--border-default)] px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-[var(--surface-2)]"
      >
        Done, drink received
      </button>
    </div>
  );
}

// Already-redeemed-this-period state, shows the same countdown.
function LockedCountdown({
  name,
  reason,
  nextAvailableAt,
  variant,
  pinkIcon,
}: {
  name: string;
  reason: string;
  nextAvailableAt: string;
  variant: "row" | "hero";
  pinkIcon?: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const countdownText = formatCountdown(new Date(nextAvailableAt).getTime(), now);
  const containerCls =
    variant === "hero"
      ? "flex items-center gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface-2)] p-4"
      : "flex items-center gap-3 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface-2)] p-3";
  return (
    <div className={containerCls}>
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--surface)] ${pinkIcon ? "text-pink-500" : "text-[var(--muted)]"}`}
      >
        <HourglassIcon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[var(--foreground)]">{name}</p>
        <p className="text-xs text-[var(--muted)]">{reason}</p>
        <p className="mt-1 text-xs text-[var(--muted)]">
          Next available in{" "}
          <span className="font-semibold tabular-nums text-[var(--foreground)]">
            {countdownText}
          </span>
        </p>
      </div>
    </div>
  );
}

// White gift-icon fallback for the hero card's idle state, replacing the
// 🎁/🍺 emoji glyphs, swapped for a venue-configured benefit.iconEmoji.
function GiftIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H4.5a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
    </svg>
  );
}

// Hourglass icon for the redemption-limit-reached (locked) state. Replaces
// the ⏳ emoji which renders inconsistently across OS + browsers (native
// coloured glyph on iOS/macOS, mono on Windows, missing on some Android).
function HourglassIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M6 3h12" />
      <path d="M6 21h12" />
      <path d="M6 3v3.5a6 6 0 003 5.2 6 6 0 00-3 5.2V21" />
      <path d="M18 3v3.5a6 6 0 01-3 5.2 6 6 0 013 5.2V21" />
    </svg>
  );
}
